<template>
  <footer class="bg-dim pt-9 pb-5 text-white">
    <AppContainer>
      <div class="main flex gap-10">
        <div class="flex col items-center gap-2">
          <AppImg src="/images/logo-white.svg" alt="Logo" width="160" height="45" class="logo" />
          <DesktopOnly class="payment-logos flex justify-center mt-4">
            <PaymentLogos />
          </DesktopOnly>
        </div>
        <div class="items flex grow justify-around gap-5">
          <DesktopOnly>
            <h4 class="mb-6">{{ $tt('footer.explore') }}</h4>
            <PageLinksList class="gap-5" />
          </DesktopOnly>
          <DesktopOnly>
            <div>
              <h4 class="mb-6">{{ $tt('footer.visas') }}</h4>
              <div class="flex col gap-5">
                <VisaCountryLinkList source="footer" />
              </div>
            </div>
          </DesktopOnly>
          <DesktopOnly>
            <div class="flex justify-between gap-3">
              <div class="equal">
                <h4 class="mb-6">{{ $tt('footer.contact') }}</h4>
                <div class="flex col gap-5">
                  <span>
                    <AppIcon icon="mdi:whatsapp" start />
                    <WhatsappLink class="plain">{{ $tt('whatsapp') }}</WhatsappLink>
                  </span>
                  <span v-if="telegram">
                    <AppIcon icon="uil:telegram-alt" start />
                    <a :href="telegram" class="plain" target="_blank">{{ $tt('socials.telegram') }}</a>
                  </span>
                  <span>
                    <AppIcon icon="uil:phone" start />
                    <PhoneLink class="plain" />
                  </span>
                  <span>
                    <AppIcon icon="clarity:email-line" start />
                    <SupportEmailLink class="plain" />
                  </span>
                  <span>
                    <AppIcon icon="bx:chat" start />
                    <a @click="openChatboxBus.emit" class="plain">{{ $tt('chat') }}</a>
                  </span>
                  <IconLink icon="bx:support" to="/contact">
                    {{ $tt('support') }}
                  </IconLink>
                  <DesktopOnly>
                    <SocialLinks size="medium" class="mt-4" />
                  </DesktopOnly>
                </div>
              </div>
            </div>
          </DesktopOnly>

          <MobileOnly>
            <v-expansion-panels v-model="openPanelIndex" bg-color="dim" multiple flat>
              <NavBarMobilePanel :header="$tt('footer.explore')">
                <PageLinksList class="gap-5" />
              </NavBarMobilePanel>
              <NavBarMobilePanel :header="$tt('footer.visas')">
                <VisaCountryLinkList source="footer" />
              </NavBarMobilePanel>
              <NavBarMobilePanel :header="$tt('footer.contact')">
                <div class="flex col gap-3">
                  <WhatsappLink>
                    <AppBtn prepend-icon="mdi:whatsapp" block white>
                      {{ $tt('whatsapp') }}
                    </AppBtn>
                  </WhatsappLink>
                  <a v-if="telegram" :href="telegram" target="_blank">
                    <AppBtn prepend-icon="uil:telegram-alt" block white>
                      {{ $tt('socials.telegram') }}
                    </AppBtn>
                  </a>
                  <PhoneLink>
                    <AppBtn prepend-icon="uil:phone" block white>
                      {{ phone.display }}
                    </AppBtn>
                  </PhoneLink>
                  <SupportEmailLink>
                    <AppBtn prepend-icon="clarity:email-line" block white>
                      {{ supportEmail }}
                    </AppBtn>
                  </SupportEmailLink>
                  <AppBtn @click="openChatboxBus.emit" prepend-icon="bx:chat" block white>
                    {{ $tt('chat') }}
                  </AppBtn>
                  <AppLink to="/contact">
                    <AppBtn prepend-icon="bx:support" block white>
                      {{ $tt('support') }}
                    </AppBtn>
                  </AppLink>
                </div>
                <div class="mt-7 mb-4">
                  <h4 class="mb-5">{{ $tt('follow_us') }}</h4>
                  <SocialLinks size="large" />
                </div>
              </NavBarMobilePanel>
            </v-expansion-panels>
          </MobileOnly>
        </div>
      </div>

      <div class="bottom flex justify-center mt-8 pt-6">
        <MobileOnly>
          <PaymentLogos />
        </MobileOnly>
        <p class="legal" :class="{ 'text-center': !getAcknowledgedBranches($branch).length }">
          {{ companyAddressText }} 
          <CountryFlag :code="baseCountry" size="x-small" start />
          <br>
          <strong>{{ $tt(name) }} © {{ getCurrentYear() }}</strong>
        </p>
        <div class="mb-2">
          <LocaleSelect v-if="!isBlogPost" />
        </div>
      </div>
    </AppContainer>
  </footer>
</template>

<script setup lang="ts">
import { getCurrentYear } from '~/helpers/date';
import { getAcknowledgedBranches } from '~/helpers/branch';

const { companyAddressText, telegram, phone, supportEmail } = useCommunication();
const { locale, baseCountry } = useBranch();
const { openChatboxBus } = useEmitter();
const { isBlogPost } = useRouting();

const name = locale == 'ru' ? 'brand.name' : 'about_page.company_name';
const openPanelIndex = ref([0, 1, 2]);
</script>

<style scoped lang="scss">
a, :deep(a), p, span, :deep(h4) {
  color: white;
}
.logo {
  max-width: fit-content;
}
.payment-logos {
  max-width: 160px;
}
.items > div {
  min-width: 160px;
}
.bottom {
  border-top: 1px solid #fff;
  .legal {
    line-height: var(--lh-md);
    font-size: 16px;
    flex: 1;
  }
}

@media (max-width: 850px) {
  footer {
    padding: var(--space-7) 0 var(--space-7) !important;
  }
  .main {
    flex-direction: column;
    padding: var(--space-2);
    gap: var(--space-7);
    h4 {
      font-size: 22px;
    }
    .logo {
      margin-bottom: 0;
    }
  }
  .items {
    flex-direction: column-reverse;
    gap: var(--space-7);
  }
  .bottom {
    margin-top: 0 !important;
    flex-direction: column-reverse;
    align-items: center;
    text-align: center;
    .legal {
      margin: var(--space-2) 0 var(--space-4);
    }
  }
  :deep(.v-expansion-panel--active) {
    margin-top: 0 !important;
  }
}
</style>